import { AsyncPipe, CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Injector,
  OnChanges,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
  afterNextRender,
  inject,
  input,
  signal,
} from '@angular/core';

import { BehaviorSubject, EMPTY, Observable, Subject, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs';

import { ENotificationCode, LAZY_SERVICE_TOKEN } from '@camelot/notification';
import { TranslatePipe } from '@camelot/translation';
import { EmptyComponent, ErrorComponent, LoaderComponent, TitleComponent } from '@camelot/ui';
import { CamBaseComponent, RequestState } from '@camelot/utils';
import { convertBlocksToHtml } from '@camelot/wysiswyg';

import { CamCommunicationsService } from '../../services/communications.service';
import {
  Communication,
  CommunicationParticipantExtended,
  CommunicationStatus,
  CommunicationType,
} from '../../services/dto/communication';
import { CommunicationPayloadInput, CorrespondentInputDto } from '../../services/dto/post/communication';
import { CamTranslationCommunication } from '../../translation.service';
import { Filter, FiltersComponent } from '../filters/filters.component';
import { ListComponent } from '../list/list.component';
import { CorrespondentInput } from '../message-box-container/input/input.component';
import {
  MessageBoxContainerComponent,
  MessageBoxOutputMessage,
} from '../message-box-container/message-box-container.component';
import { communications$, participants$ } from './conversation.helper';

const POLLING_IDLE_SECONDS = 60;
const POLLING_PENDING_SECONDS = 5;

@Component({
  selector: 'cam-conversation',
  templateUrl: './conversation.component.html',
  styleUrls: ['./conversation.component.scss'],
  imports: [
    CommonModule,
    AsyncPipe,
    TranslatePipe,
    TitleComponent,
    LoaderComponent,
    ErrorComponent,
    EmptyComponent,
    FiltersComponent,
    ListComponent,
    MessageBoxContainerComponent,
  ],
})
export class ConversationComponent extends CamBaseComponent implements OnChanges, AfterViewInit {
  taskId = input.required<string>();

  contactId$ = input.required<Observable<string | null>>();

  statusTemplate = input.required<TemplateRef<any>>();

  readonly = input(false);

  canSendMail = input(true);

  @ViewChildren(ListComponent) listComponents!: QueryList<ListComponent>;
  @ViewChild('scrollMe', { static: false }) scrollMe!: ElementRef<HTMLElement>;

  readonly scrollTop = signal(0);

  public filters$ = new Subject<Filter>();

  public communications$: Observable<Communication[]> = of([]);

  public participants$: Observable<CommunicationParticipantExtended[]> | null = null;

  public outOfBox = signal(false);

  readonly clearBox$ = new Subject<void>();

  private readonly _refresh$ = new Subject<void>();

  private readonly _pollingInSecond$ = new BehaviorSubject(POLLING_IDLE_SECONDS);

  private _conversationService = inject(CamCommunicationsService);

  private _injector = inject(Injector);

  protected _notificationService = inject(LAZY_SERVICE_TOKEN);

  public replyTo: { communication: Communication; isForward: boolean } | null = null;
  public seeAll = true;

  public isChatEnabled = signal<boolean>(false);

  public messageBoxRequestState = new RequestState();

  constructor() {
    super();
    CamTranslationCommunication.getInstance();

    // souscriptions enregistrées une seule fois : le switchMap remplace le watcher précédent
    // au lieu d'en empiler un de plus à chaque changement de tâche ou envoi de message
    this._registerSubscription(
      this._refresh$
        .pipe(
          switchMap(() =>
            this._conversationService
              .watchingRequest({
                query$: this._conversationService.fetchConversationByTaskId$(this.taskId()),
                timerInSecond: this._pollingInSecond$,
                watcher$: this._conversationService.watchConversationByTaskId$(this.taskId()),
              })
              .pipe(
                catchError((error: HttpErrorResponse) => {
                  this.requestState.onError(error.status, error.statusText);
                  return EMPTY;
                })
              )
          )
        )
        .subscribe(() => this.requestState.completed())
    );

    this._registerSubscription(
      this._refresh$
        .pipe(
          switchMap(() => this.communications$),
          map(list => (list ?? []).some(c => c.type === CommunicationType.Chat)),
          distinctUntilChanged()
        )
        .subscribe(hasChat => this.isChatEnabled.set(hasChat))
    );

    // liste non filtrée : un envoi masqué par les filtres doit quand même accélérer le polling
    this._registerSubscription(
      this._refresh$
        .pipe(
          switchMap(() => communications$(this._conversationService, this.taskId())),
          map(list => list.some(c => c.status === CommunicationStatus.Pending)),
          distinctUntilChanged(),
          map(hasPending => (hasPending ? POLLING_PENDING_SECONDS : POLLING_IDLE_SECONDS))
        )
        .subscribe(seconds => this._pollingInSecond$.next(seconds))
    );

    this._registerSubscription(
      this._refresh$.pipe(switchMap(() => this.communications$)).subscribe(() => this._scrollToBottom())
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['taskId']?.previousValue !== changes['taskId']?.currentValue) {
      this.communications$ = communications$(this._conversationService, this.taskId(), this.filters$);
      this.participants$ = participants$(this._conversationService, this.taskId(), this.contactId$());

      this._fetch();
    }
  }

  ngAfterViewInit() {
    this._registerSubscription(this.listComponents.changes.subscribe(() => this._scrollToBottom()));

    this._scrollToBottom();
  }

  public showAll() {
    this.seeAll = true;
  }

  public setReplyTo(replyTo: { communication: Communication; isForward: boolean }) {
    this.replyTo = replyTo;
  }
  public sendMessage(data: MessageBoxOutputMessage) {
    const conversationId = this._conversationService.communicationByTaskId.get(this.taskId())?.id;

    if (!conversationId) {
      return;
    }
    this.messageBoxRequestState.asked();
    const com: CommunicationPayloadInput = {
      type: data.type,
      toCorrespondents: this._mapCorrespondentInput(data.tos),
      ccCorrespondents: this._mapCorrespondentInput(data.ccs),
      taggedCorrespondents: this._mapCorrespondentInput(data.tags),
      documentIds: data.attachments.map(att => att.id),
      attachmentUrls: data.attachments.map(att => att.url),
      responseToCommunicationId: this.replyTo?.communication.id,
      body: data.type === CommunicationType.Mail ? convertBlocksToHtml(data.blocks) : JSON.stringify(data.blocks),

      conversationId,
    };
    this._conversationService.createConversation$(com).subscribe({
      complete: () => {
        this._notificationService.addNotification('notification.common.success', ENotificationCode.success);
        this.clearBox$.next();
        this.replyTo = null;
        this.outOfBox.set(false);
        this._fetch();

        this.messageBoxRequestState.completed();
      },
      error: () => {
        this.messageBoxRequestState.completed();
      },
    });
  }

  private _scrollToBottom() {
    afterNextRender(
      () => {
        const container = this.scrollMe?.nativeElement;
        if (container) {
          this.scrollTop.set(container.scrollHeight);
        }
      },
      { injector: this._injector }
    );
  }

  private _fetch() {
    this.requestState.asked();
    this._refresh$.next();
  }

  private _mapCorrespondentInput(Correspondents: CorrespondentInput[]): CorrespondentInputDto[] {
    return Correspondents.map(correspondent => {
      const { data, ...rest } = correspondent;
      return rest;
    });
  }
}
